import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { coursesAPI } from '../services/api';
import { 
  BookOpen, Plus, Trash2, Save, X, Video, FileText, 
  HelpCircle, Upload, Loader2, CheckCircle, AlertCircle 
} from 'lucide-react';

interface Lesson {
  title: string;
  description: string;
  content: string;
  videoUrl: string;
  subtitleUrl?: string;
  subtitleLanguage?: string;
  duration: number;
  order: number;
  resources: Array<{ title: string; url: string; type: string }>;
  quiz: {
    questions: Array<{
      question: string;
      options: string[];
      correctAnswer: number;
      explanation: string;
    }>;
  };
}

interface CourseData {
  title: string;
  description: string;
  shortDescription: string;
  category: string;
  subcategory: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  language: string;
  thumbnail: string;
  image: string;
  video: string;
  subtitleUrl?: string;
  subtitleLanguage?: string;
  price: number;
  isFree: boolean;
  isPublished: boolean;
  tags: string[];
  certificateAvailable: boolean;
  whatStudentsWillLearn: string[];
  skillsTheyWillGain: string[];
  downloadablePDFs: Array<{ title: string; url: string; description: string; fileSize?: number }>;
  slides: Array<{ title: string; url: string; description: string; slideNumber?: number }>;
  externalLinks: Array<{ title: string; url: string; description: string; type: string }>;
  lessons: Lesson[];
  dates?: Array<{ date: string; startTime: string; endTime: string }>;
}

const CreateCourse: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { user } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'lessons'>('basic');
  const [editingLessonIndex, setEditingLessonIndex] = useState<number | null>(null);
  const [editingQuizIndex, setEditingQuizIndex] = useState<number | null>(null);

  const [courseData, setCourseData] = useState<CourseData>({
    title: '',
    description: '',
    shortDescription: '',
    category: '',
    subcategory: '',
    level: 'beginner',
    language: 'fr',
    thumbnail: '',
    image: '',
    video: '',
    subtitleUrl: '',
    subtitleLanguage: 'fr',
    price: 0,
    isFree: true,
    isPublished: true, // Auto-publish courses by default
    tags: [],
    certificateAvailable: false,
    whatStudentsWillLearn: [],
    skillsTheyWillGain: [],
    downloadablePDFs: [],
    slides: [],
    externalLinks: [],
    lessons: [],
    dates: []
  });

  // Define subcategories for each category
  const categorySubcategories: { [key: string]: string[] } = {
    'Development': [
      'Web Development',
      'Mobile Development',
      'Backend Development',
      'Frontend Development',
      'Full Stack Development',
      'DevOps',
      'Game Development',
      'Software Engineering',
      'Database Development',
      'API Development',
      'Others'
    ],
    'Technology': [
      'Cloud Computing',
      'Artificial Intelligence',
      'Machine Learning',
      'Data Science',
      'Cybersecurity',
      'Blockchain',
      'IoT',
      'Robotics',
      'Virtual Reality',
      'Augmented Reality',
      'Others'
    ],
    'Business': [
      'Entrepreneurship',
      'Management',
      'Finance',
      'Accounting',
      'Project Management',
      'Business Strategy',
      'Operations',
      'Supply Chain',
      'Human Resources',
      'Business Analysis',
      'Others'
    ],
    'Marketing': [
      'Digital Marketing',
      'Social Media Marketing',
      'Content Marketing',
      'SEO',
      'Email Marketing',
      'Advertising',
      'Brand Management',
      'Market Research',
      'Public Relations',
      'E-commerce Marketing',
      'Others'
    ],
    'Agriculture': [
      'Crop Production',
      'Livestock Management',
      'Organic Farming',
      'Agricultural Technology',
      'Sustainable Agriculture',
      'Agricultural Business',
      'Food Processing',
      'Agricultural Engineering',
      'Agricultural Economics',
      'Agribusiness',
      'Others'
    ],
    'Others': [
      'General',
      'Personal Development',
      'Language Learning',
      'Arts & Design',
      'Music',
      'Photography',
      'Writing',
      'Health & Fitness',
      'Education',
      'Other'
    ]
  };

  const [newDate, setNewDate] = useState('');
  const [newStartTime, setNewStartTime] = useState('');
  const [newEndTime, setNewEndTime] = useState('');

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [autoGenerateSubtitles] = useState<boolean>(true); // Always enabled - subtitles are automatically generated

  const [newTag, setNewTag] = useState('');
  const [currentLesson, setCurrentLesson] = useState<Lesson>({
    title: '',
    description: '',
    content: '',
    videoUrl: '',
    subtitleUrl: '',
    subtitleLanguage: 'fr',
    duration: 0,
    order: 0,
    resources: [],
    quiz: { questions: [] }
  });
  const [lessonVideoFile, setLessonVideoFile] = useState<File | null>(null);
  const [lessonVideoPreview, setLessonVideoPreview] = useState<string | null>(null);
  
  // State for slide images
  const [slideImageFiles, setSlideImageFiles] = useState<{ [index: number]: File | null }>({});
  const [slideImagePreviews, setSlideImagePreviews] = useState<{ [index: number]: string | null }>({});
  const [lessonAutoGenerateSubtitles, setLessonAutoGenerateSubtitles] = useState<boolean>(true);

  const [currentQuestion, setCurrentQuestion] = useState({
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    explanation: ''
  });

  // Load course data in edit mode
  useEffect(() => {
    const loadCourseData = async () => {
      if (id) {
        setIsEditMode(true);
        try {
          const response = await coursesAPI.getCourse(id);
          const course = response.data.data.course;
          
          // Filter out incomplete lessons when loading
          const validLessons = (course.lessons || []).filter(
            (lesson: any) => lesson.title && lesson.title.trim() && lesson.content && lesson.content.trim()
          );
          
          setCourseData({
            title: course.title || '',
            description: course.description || '',
            shortDescription: course.shortDescription || '',
            category: course.category || '',
            subcategory: course.subcategory || '',
            level: course.level || 'beginner',
            language: course.language || 'fr',
            thumbnail: course.thumbnail || '',
            image: course.image || '',
            video: course.video || '',
            subtitleUrl: course.subtitleUrl || '',
            subtitleLanguage: course.subtitleLanguage || 'fr',
            price: course.price || 0,
            isFree: course.isFree !== undefined ? course.isFree : true,
            isPublished: course.isPublished !== undefined ? course.isPublished : true, // Default to published
            tags: course.tags || [],
            certificateAvailable: course.certificateAvailable || false,
            whatStudentsWillLearn: course.whatStudentsWillLearn || [],
            skillsTheyWillGain: course.skillsTheyWillGain || [],
            downloadablePDFs: course.downloadablePDFs || [],
            slides: course.slides || [],
            externalLinks: course.externalLinks || [],
            lessons: validLessons.map((lesson: any, index: number) => ({
              title: lesson.title || '',
              description: lesson.description || '',
              content: lesson.content || '',
              videoUrl: lesson.videoUrl || '',
              subtitleUrl: lesson.subtitleUrl || '',
              subtitleLanguage: lesson.subtitleLanguage || 'fr',
              duration: lesson.duration || 0,
              order: lesson.order !== undefined ? lesson.order : index,
              resources: lesson.resources || [],
              quiz: lesson.quiz || { questions: [] }
            })),
            dates: course.dates && course.dates.length > 0 
              ? course.dates.map((d: any) => ({
                  date: d.date || (course.startDate ? new Date(course.startDate).toISOString().split('T')[0] : ''),
                  startTime: d.startTime || (course.startDate ? new Date(course.startDate).toTimeString().slice(0, 5) : '09:00'),
                  endTime: d.endTime || (course.endDate ? new Date(course.endDate).toTimeString().slice(0, 5) : '17:00')
                }))
              : course.startDate ? [{
                  date: new Date(course.startDate).toISOString().split('T')[0],
                  startTime: course.startDate ? new Date(course.startDate).toTimeString().slice(0, 5) : '09:00',
                  endTime: course.endDate ? new Date(course.endDate).toTimeString().slice(0, 5) : '17:00'
                }] : []
          });
          
          // Initialize slide image previews if slides have URLs
          if (course.slides && course.slides.length > 0) {
            const previews: { [index: number]: string | null } = {};
            course.slides.forEach((slide: any, index: number) => {
              if (slide.url) {
                previews[index] = slide.url;
              }
            });
            setSlideImagePreviews(previews);
          }
        } catch (error: any) {
          console.error('Error loading course:', error);
          setError('Failed to load course data');
        }
      }
    };
    
    loadCourseData();
  }, [id]);

  const handleBasicInfoChange = (field: keyof CourseData, value: any) => {
    setCourseData(prev => ({ ...prev, [field]: value }));
  };

  const addDate = () => {
    if (newDate && !courseData.dates?.some(d => d.date === newDate)) {
      setCourseData(prev => ({
        ...prev,
        dates: [...(prev.dates || []), {
          date: newDate,
          startTime: newStartTime || '09:00',
          endTime: newEndTime || '17:00'
        }]
      }));
      setNewDate('');
      setNewStartTime('');
      setNewEndTime('');
    }
  };

  const removeDate = (dateToRemove: string) => {
    setCourseData(prev => ({
      ...prev,
      dates: prev.dates?.filter(d => d.date !== dateToRemove) || []
    }));
  };

  const updateDateTime = (date: string, field: 'startTime' | 'endTime', value: string) => {
    setCourseData(prev => ({
      ...prev,
      dates: prev.dates?.map(d => 
        d.date === date ? { ...d, [field]: value } : d
      ) || []
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !courseData.tags.includes(newTag.trim().toLowerCase())) {
      setCourseData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim().toLowerCase()]
      }));
      setNewTag('');
    }
  };

  // State for course objectives inputs
  const [whatStudentsWillLearnInput, setWhatStudentsWillLearnInput] = useState('');
  const [skillsTheyWillGainInput, setSkillsTheyWillGainInput] = useState('');

  const addWhatStudentsWillLearn = () => {
    if (whatStudentsWillLearnInput.trim()) {
      setCourseData(prev => ({
        ...prev,
        whatStudentsWillLearn: [...prev.whatStudentsWillLearn, whatStudentsWillLearnInput.trim()]
      }));
      setWhatStudentsWillLearnInput('');
    }
  };

  const removeWhatStudentsWillLearn = (index: number) => {
    setCourseData(prev => ({
      ...prev,
      whatStudentsWillLearn: prev.whatStudentsWillLearn.filter((_, i) => i !== index)
    }));
  };

  const addSkillsTheyWillGain = () => {
    if (skillsTheyWillGainInput.trim()) {
      setCourseData(prev => ({
        ...prev,
        skillsTheyWillGain: [...prev.skillsTheyWillGain, skillsTheyWillGainInput.trim()]
      }));
      setSkillsTheyWillGainInput('');
    }
  };

  const removeSkillsTheyWillGain = (index: number) => {
    setCourseData(prev => ({
      ...prev,
      skillsTheyWillGain: prev.skillsTheyWillGain.filter((_, i) => i !== index)
    }));
  };

  const removeTag = (tag: string) => {
    setCourseData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const addLesson = async () => {
    // Validate lesson has required fields
    if (!currentLesson.title || !currentLesson.title.trim()) {
      alert('Please enter a lesson title');
      return;
    }
    if (!currentLesson.content || !currentLesson.content.trim()) {
      alert('Please enter lesson content');
      return;
    }
    
    // Upload lesson video if provided
    let lessonVideoUrl = currentLesson.videoUrl || '';
    let autoGeneratedLessonSubtitleUrl: string | null = null;
    if (lessonVideoFile) {
      try {
        const formData = new FormData();
        formData.append('video', lessonVideoFile);
        formData.append('autoGenerateSubtitles', lessonAutoGenerateSubtitles.toString());
        formData.append('subtitleLanguage', currentLesson.subtitleLanguage || 'fr');
        
        const uploadResponse = await coursesAPI.uploadCourseVideo(formData);
        lessonVideoUrl = uploadResponse.data.data.video || uploadResponse.data.data.url;
        // If subtitles were auto-generated, use them (always use subtitleUrl if provided)
        if (uploadResponse.data.data.subtitleUrl) {
          autoGeneratedLessonSubtitleUrl = uploadResponse.data.data.subtitleUrl;
          console.log('✅ Automatic subtitles generated for lesson:', autoGeneratedLessonSubtitleUrl);
          console.log('Lesson subtitle generated flag:', uploadResponse.data.data.subtitleGenerated);
        } else {
          console.warn('No subtitleUrl in lesson upload response:', uploadResponse.data.data);
        }
      } catch (uploadErr: any) {
        setError(uploadErr.response?.data?.message || 'Error uploading lesson video');
        return;
      }
    }
    
    // Subtitles are automatically generated from video - no manual upload
    let lessonSubtitleUrl = autoGeneratedLessonSubtitleUrl || currentLesson.subtitleUrl || '';
    
    const newLesson: Lesson = {
      ...currentLesson,
      videoUrl: lessonVideoUrl,
      subtitleUrl: lessonSubtitleUrl || undefined,
      subtitleLanguage: currentLesson.subtitleLanguage || 'fr',
      order: courseData.lessons.length
    };
    setCourseData(prev => ({
      ...prev,
      lessons: [...prev.lessons, newLesson]
    }));
    setCurrentLesson({
      title: '',
      description: '',
      content: '',
      videoUrl: '',
      subtitleUrl: '',
      subtitleLanguage: 'fr',
      duration: 0,
      order: 0,
      resources: [],
      quiz: { questions: [] }
    });
    setLessonVideoFile(null);
    setLessonVideoPreview(null);
  };

  const editLesson = (index: number) => {
    setCurrentLesson(courseData.lessons[index]);
    setEditingLessonIndex(index);
    setLessonVideoFile(null); // Reset video file when editing
    setLessonVideoPreview(null); // Reset video preview when editing
  };

  const updateLesson = async () => {
    if (editingLessonIndex !== null) {
      // Validate lesson has required fields
      if (!currentLesson.title || !currentLesson.title.trim()) {
        alert('Please enter a lesson title');
        return;
      }
      if (!currentLesson.content || !currentLesson.content.trim()) {
        alert('Please enter lesson content');
        return;
      }

      // Upload lesson video if provided
      let lessonVideoUrl = currentLesson.videoUrl || '';
      let autoGeneratedLessonSubtitleUrl: string | null = null;
      if (lessonVideoFile) {
        try {
          const formData = new FormData();
          formData.append('video', lessonVideoFile);
          formData.append('autoGenerateSubtitles', lessonAutoGenerateSubtitles.toString());
          formData.append('subtitleLanguage', currentLesson.subtitleLanguage || 'fr');
          
          const uploadResponse = await coursesAPI.uploadCourseVideo(formData);
          lessonVideoUrl = uploadResponse.data.data.video || uploadResponse.data.data.url;
          // If subtitles were auto-generated, use them (always use subtitleUrl if provided)
          if (uploadResponse.data.data.subtitleUrl) {
            autoGeneratedLessonSubtitleUrl = uploadResponse.data.data.subtitleUrl;
            console.log('✅ Automatic subtitles generated for lesson:', autoGeneratedLessonSubtitleUrl);
            console.log('Lesson subtitle generated flag:', uploadResponse.data.data.subtitleGenerated);
          } else {
            console.warn('No subtitleUrl in lesson update upload response:', uploadResponse.data.data);
          }
        } catch (uploadErr: any) {
          setError(uploadErr.response?.data?.message || 'Error uploading lesson video');
          return;
        }
      }

      // Subtitles are automatically generated from video - no manual upload
      let lessonSubtitleUrl = autoGeneratedLessonSubtitleUrl || currentLesson.subtitleUrl || '';
      
      const updatedLessons = [...courseData.lessons];
      updatedLessons[editingLessonIndex] = {
        ...currentLesson,
        videoUrl: lessonVideoUrl,
        subtitleUrl: lessonSubtitleUrl || undefined,
        subtitleLanguage: currentLesson.subtitleLanguage || 'fr'
      };
      setCourseData(prev => ({ ...prev, lessons: updatedLessons }));
      setCurrentLesson({
        title: '',
        description: '',
        content: '',
        videoUrl: '',
        subtitleUrl: '',
        subtitleLanguage: 'fr',
        duration: 0,
        order: 0,
        resources: [],
        quiz: { questions: [] }
      });
      setLessonVideoFile(null);
      setLessonVideoPreview(null);
      setEditingLessonIndex(null);
    }
  };

  const deleteLesson = (index: number) => {
      if (window.confirm('Are you sure you want to delete this lesson?')) {
      const updatedLessons = courseData.lessons.filter((_, i) => i !== index);
      // Réorganiser les ordres
      updatedLessons.forEach((lesson, i) => {
        lesson.order = i;
      });
      setCourseData(prev => ({ ...prev, lessons: updatedLessons }));
    }
  };

  const addQuestion = () => {
    if (currentQuestion.question && currentQuestion.options.every(opt => opt.trim())) {
      const newQuestion = { ...currentQuestion };
      const updatedLesson = {
        ...currentLesson,
        quiz: {
          questions: [...currentLesson.quiz.questions, newQuestion]
        }
      };
      setCurrentLesson(updatedLesson);
      setCurrentQuestion({
        question: '',
        options: ['', '', '', ''],
        correctAnswer: 0,
        explanation: ''
      });
      setEditingQuizIndex(null);
    }
  };

  const editQuestion = (index: number) => {
    setCurrentQuestion(currentLesson.quiz.questions[index]);
    setEditingQuizIndex(index);
  };

  const updateQuestion = () => {
    if (editingQuizIndex !== null) {
      const updatedQuestions = [...currentLesson.quiz.questions];
      updatedQuestions[editingQuizIndex] = currentQuestion;
      setCurrentLesson(prev => ({
        ...prev,
        quiz: { questions: updatedQuestions }
      }));
      setCurrentQuestion({
        question: '',
        options: ['', '', '', ''],
        correctAnswer: 0,
        explanation: ''
      });
      setEditingQuizIndex(null);
    }
  };

  const deleteQuestion = (index: number) => {
    const updatedQuestions = currentLesson.quiz.questions.filter((_, i) => i !== index);
    setCurrentLesson(prev => ({
      ...prev,
      quiz: { questions: updatedQuestions }
    }));
  };

  const addResource = () => {
    const newResource = { title: '', url: '', type: 'link' };
    setCurrentLesson(prev => ({
      ...prev,
      resources: [...prev.resources, newResource]
    }));
  };

  const updateResource = (index: number, field: string, value: string) => {
    const updatedResources = [...currentLesson.resources];
    updatedResources[index] = { ...updatedResources[index], [field]: value };
    setCurrentLesson(prev => ({ ...prev, resources: updatedResources }));
  };

  const deleteResource = (index: number) => {
    setCurrentLesson(prev => ({
      ...prev,
      resources: prev.resources.filter((_, i) => i !== index)
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

    if (file.size > maxSize) {
      setError('Image is too large. Maximum size is 5MB.');
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      setError('Invalid image format. Please use JPEG, PNG, GIF, or WebP.');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    handleBasicInfoChange('image', '');
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 500 * 1024 * 1024; // 500MB
    const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo'];

    if (file.size > maxSize) {
      setError('Video is too large. Maximum size is 500MB.');
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      setError('Invalid video format. Please use MP4, WebM, OGG, MOV, or AVI.');
      return;
    }

    setVideoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setVideoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removeVideo = () => {
    setVideoFile(null);
    setVideoPreview(null);
    handleBasicInfoChange('video', '');
  };


  const handleLessonVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 500 * 1024 * 1024; // 500MB
    const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo'];

    if (file.size > maxSize) {
      setError('Video is too large. Maximum size is 500MB.');
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      setError('Invalid video format. Please use MP4, WebM, OGG, MOV, or AVI.');
      return;
    }

    setLessonVideoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLessonVideoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removeLessonVideo = () => {
    setLessonVideoFile(null);
    setLessonVideoPreview(null);
    setCurrentLesson(prev => ({ ...prev, videoUrl: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!courseData.title.trim()) {
      setError('Course title is required');
      return;
    }

    // Filter out incomplete lessons (those without title or content)
    const validLessons = courseData.lessons.filter(
      (lesson) => lesson.title && lesson.title.trim() && lesson.content && lesson.content.trim()
    );

    if (validLessons.length === 0) {
      setError('Please add at least one complete lesson with title and content');
      return;
    }

    // Validate each lesson has required fields
    for (let i = 0; i < validLessons.length; i++) {
      const lesson = validLessons[i];
      if (!lesson.title || !lesson.title.trim()) {
        setError(`Lesson ${i + 1} is missing a title`);
        return;
      }
      if (!lesson.content || !lesson.content.trim()) {
        setError(`Lesson ${i + 1} is missing content`);
        return;
      }
      // Ensure order is set correctly
      if (lesson.order === undefined || lesson.order === null) {
        lesson.order = i;
      }
    }

    try {
      setLoading(true);
      
      // If image file exists, upload it first using FormData
      let imageUrl = courseData.image;
      if (imageFile) {
        const formData = new FormData();
        formData.append('image', imageFile);
        
        try {
          const uploadResponse = await coursesAPI.uploadCourseImage(formData);
          imageUrl = uploadResponse.data.data.image || uploadResponse.data.data.url;
        } catch (uploadErr: any) {
          setError(uploadErr.response?.data?.message || 'Error uploading image');
          setLoading(false);
          return;
        }
      }

      // If video file exists, upload it using FormData
      let videoUrl = courseData.video;
      let autoGeneratedSubtitleUrl: string | null = null;
      if (videoFile) {
        const formData = new FormData();
        formData.append('video', videoFile);
        formData.append('autoGenerateSubtitles', autoGenerateSubtitles.toString());
        formData.append('subtitleLanguage', courseData.subtitleLanguage || 'fr');
        
        try {
          const uploadResponse = await coursesAPI.uploadCourseVideo(formData);
          videoUrl = uploadResponse.data.data.video || uploadResponse.data.data.url;
          // If subtitles were auto-generated, use them (always use subtitleUrl if provided)
          if (uploadResponse.data.data.subtitleUrl) {
            autoGeneratedSubtitleUrl = uploadResponse.data.data.subtitleUrl;
            console.log('✅ Automatic subtitles generated:', autoGeneratedSubtitleUrl);
            console.log('Subtitle generated flag:', uploadResponse.data.data.subtitleGenerated);
            // Update courseData with the generated subtitle URL
            setCourseData(prev => ({ ...prev, subtitleUrl: autoGeneratedSubtitleUrl || undefined }));
          } else {
            console.warn('No subtitleUrl in upload response:', uploadResponse.data.data);
          }
        } catch (uploadErr: any) {
          setError(uploadErr.response?.data?.message || 'Error uploading video');
          setLoading(false);
          return;
        }
      }

      // Subtitles are automatically generated from video - no manual upload
      let subtitleUrl: string | undefined = autoGeneratedSubtitleUrl || courseData.subtitleUrl;
      
      // Log before saving
      console.log('Saving course with subtitleUrl:', subtitleUrl);

      // Upload slide images if any
      const uploadedSlides = await Promise.all(
        courseData.slides.map(async (slide, index) => {
          const slideImageFile = slideImageFiles[index];
          let slideUrl = slide.url;
          
          if (slideImageFile) {
            try {
              const formData = new FormData();
              formData.append('image', slideImageFile);
              
              const uploadResponse = await coursesAPI.uploadCourseImage(formData);
              slideUrl = uploadResponse.data.data.image || uploadResponse.data.data.url;
            } catch (uploadErr: any) {
              console.error(`Error uploading slide ${index + 1} image:`, uploadErr);
              // Continue with existing URL if upload fails
            }
          }
          
          return {
            ...slide,
            url: slideUrl
          };
        })
      );

      // Create course with all data including image and video URLs
      // Ensure arrays are properly formatted (not undefined)
      // Combine date and time into datetime string
      // Use the first date with its startTime as startDate and last date with its endTime as endDate
      let startDateTime: string | undefined = undefined;
      let endDateTime: string | undefined = undefined;
      
      if (courseData.dates && courseData.dates.length > 0) {
        const sortedDates = [...courseData.dates].sort((a, b) => a.date.localeCompare(b.date));
        const firstDateItem = sortedDates[0];
        const lastDateItem = sortedDates[sortedDates.length - 1];
        const startTime = firstDateItem.startTime || '00:00';
        const endTime = lastDateItem.endTime || '23:59';
        startDateTime = `${firstDateItem.date}T${startTime}:00`;
        endDateTime = `${lastDateItem.date}T${endTime}:00`;
      }

      // Ensure dates array is always included, even if empty
      const datesArray = Array.isArray(courseData.dates) ? courseData.dates : [];
      
      const coursePayload = {
        ...courseData,
        lessons: validLessons, // Use only valid lessons
        image: imageUrl,
        video: videoUrl,
        subtitleUrl: subtitleUrl,
        subtitleLanguage: courseData.subtitleLanguage || 'fr',
        startDate: startDateTime || undefined,
        endDate: endDateTime || undefined,
        dates: datesArray, // Always include dates array (even if empty)
        slides: uploadedSlides,
        whatStudentsWillLearn: courseData.whatStudentsWillLearn || [],
        skillsTheyWillGain: courseData.skillsTheyWillGain || [],
        // outcomesAfterCompletion is managed separately after course completion
      };
      
      console.log('=== COURSE SUBMISSION DEBUG ===');
      console.log('courseData.dates:', courseData.dates);
      console.log('courseData.dates length:', courseData.dates?.length || 0);
      console.log('courseData.dates type:', typeof courseData.dates);
      console.log('courseData.dates is array:', Array.isArray(courseData.dates));
      console.log('Saving course with dates:', coursePayload.dates);
      console.log('Saving course with isPublished:', coursePayload.isPublished);
      console.log('Course payload dates:', JSON.stringify(coursePayload.dates, null, 2));
      console.log('Full course payload:', JSON.stringify(coursePayload, null, 2));
      console.log('=== END DEBUG ===');
      
      if (isEditMode && id) {
        // Update existing course
        await coursesAPI.updateCourse(id, coursePayload);
        setSuccess(true);
        setTimeout(() => {
          navigate(`/courses/${id}`);
        }, 2000);
      } else {
        // Create new course
        const response = await coursesAPI.createCourse(coursePayload);
        setSuccess(true);
        setTimeout(() => {
          navigate(`/courses/${response.data.data._id}`);
        }, 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error creating course');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Login Required</h2>
          <p className="text-gray-600 mb-6">You must be logged in to create a course</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {isEditMode ? 'Edit Course' : 'Create a New Course'}
        </h1>
        <p className="text-gray-600">
          {isEditMode ? 'Update your course information' : 'Create and share your knowledge'}
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
          <p className="text-sm text-green-800">Course created successfully! Redirecting...</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                type="button"
                onClick={() => setActiveTab('basic')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'basic'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Basic Information
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('lessons')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'lessons'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Lessons ({courseData.lessons.length})
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Basic Info Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Title *
                  </label>
                  <input
                    type="text"
                    value={courseData.title}
                    onChange={(e) => handleBasicInfoChange('title', e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Ex: Introduction to Sustainable Agriculture"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Short Description *
                  </label>
                  <input
                    type="text"
                    value={courseData.shortDescription}
                    onChange={(e) => handleBasicInfoChange('shortDescription', e.target.value)}
                    maxLength={200}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="A brief description (max 200 characters)"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {courseData.shortDescription.length}/200 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Description *
                  </label>
                  <textarea
                    value={courseData.description}
                    onChange={(e) => handleBasicInfoChange('description', e.target.value)}
                    required
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Detailed course description..."
                  />
                </div>

                {/* Course Objectives Section */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Objectives</h3>
                  
                  {/* What students will learn */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      What students will learn
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={whatStudentsWillLearnInput}
                        onChange={(e) => setWhatStudentsWillLearnInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addWhatStudentsWillLearn();
                          }
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Enter what students will learn..."
                      />
                      <button
                        type="button"
                        onClick={addWhatStudentsWillLearn}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </button>
                    </div>
                    <div className="space-y-2">
                      {courseData.whatStudentsWillLearn.map((item, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg">
                          <span className="text-sm text-gray-700">{item}</span>
                          <button
                            type="button"
                            onClick={() => removeWhatStudentsWillLearn(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Skills they will gain */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Skills they will gain
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={skillsTheyWillGainInput}
                        onChange={(e) => setSkillsTheyWillGainInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addSkillsTheyWillGain();
                          }
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Enter skills students will gain..."
                      />
                      <button
                        type="button"
                        onClick={addSkillsTheyWillGain}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </button>
                    </div>
                    <div className="space-y-2">
                      {courseData.skillsTheyWillGain.map((item, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg">
                          <span className="text-sm text-gray-700">{item}</span>
                          <button
                            type="button"
                            onClick={() => removeSkillsTheyWillGain(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={courseData.category}
                      onChange={(e) => {
                        handleBasicInfoChange('category', e.target.value);
                        // Reset subcategory when category changes
                        handleBasicInfoChange('subcategory', '');
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="">Select a category</option>
                      <option value="Development">Development</option>
                      <option value="Technology">Technology</option>
                      <option value="Business">Business</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Agriculture">Agriculture</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>

                  {courseData.category && categorySubcategories[courseData.category] && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subcategory
                      </label>
                      <select
                        value={courseData.subcategory}
                        onChange={(e) => handleBasicInfoChange('subcategory', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="">Select a subcategory</option>
                        {categorySubcategories[courseData.category].map((subcat) => (
                          <option key={subcat} value={subcat}>
                            {subcat}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Level *
                    </label>
                    <select
                      value={courseData.level}
                      onChange={(e) => handleBasicInfoChange('level', e.target.value as any)}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Dates & Schedule
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Add dates and times for your course. These will appear in the course calendar. 
                    <strong className="text-green-600"> You must add at least one date for the course to appear in the calendar.</strong>
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                    <input
                      type="date"
                      value={newDate}
                      onChange={(e) => setNewDate(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    />
                    <input
                      type="time"
                      value={newStartTime}
                      onChange={(e) => setNewStartTime(e.target.value)}
                      placeholder="Start Time"
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <input
                      type="time"
                      value={newEndTime}
                      onChange={(e) => setNewEndTime(e.target.value)}
                      placeholder="End Time"
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={addDate}
                      disabled={!newDate}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      <Plus className="h-4 w-4" />
                      Add Date
                    </button>
                  </div>
                  {courseData.dates && courseData.dates.length > 0 ? (
                    <div className="mb-2">
                      <p className="text-xs text-green-600 font-medium">
                        ✓ {courseData.dates.length} date(s) added. These will appear in the calendar.
                      </p>
                    </div>
                  ) : (
                    <div className="mb-2">
                      <p className="text-xs text-red-600 font-medium">
                        ⚠ No dates added. Add at least one date for the course to appear in the calendar.
                      </p>
                    </div>
                  )}
                  {courseData.dates && courseData.dates.length > 0 && (
                    <div className="space-y-2 mt-4">
                      {courseData.dates.map((dateItem, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg"
                        >
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 mb-1">
                              {new Date(dateItem.date).toLocaleDateString('fr-FR', { 
                                weekday: 'short', 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">Start Time</label>
                                <input
                                  type="time"
                                  value={dateItem.startTime}
                                  onChange={(e) => updateDateTime(dateItem.date, 'startTime', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">End Time</label>
                                <input
                                  type="time"
                                  value={dateItem.endTime}
                                  onChange={(e) => updateDateTime(dateItem.date, 'endTime', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeDate(dateItem.date)}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Course preview"
                          className="w-full h-48 object-cover rounded-lg mb-2"
                        />
                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <Upload className="h-8 w-8 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">Click to upload image</p>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 5MB</p>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                        />
                      </label>
                    )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Language
                    </label>
                    <select
                      value={courseData.language}
                      onChange={(e) => handleBasicInfoChange('language', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="fr">Français</option>
                      <option value="en">English</option>
                      <option value="ar">العربية</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Course Video
                    </label>
                    {videoPreview ? (
                      <div className="relative">
                        <video
                          src={videoPreview}
                          controls
                          className="w-full h-48 object-cover rounded-lg mb-2"
                        />
                        <button
                          type="button"
                          onClick={removeVideo}
                          className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        {videoFile && (
                          <p className="text-xs text-gray-500 mt-1">
                            {videoFile.name} ({(videoFile.size / (1024 * 1024)).toFixed(2)} MB)
                          </p>
                        )}
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <Video className="h-8 w-8 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">Click to upload video file</p>
                          <p className="text-xs text-gray-500 mt-1">MP4, WebM, OGG up to 500MB</p>
                        </div>
                        <input
                          type="file"
                          accept="video/*"
                          onChange={handleVideoChange}
                          className="hidden"
                        />
                      </label>
                    )}
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Or provide a video URL (YouTube, Vimeo, MP4, etc.)
                      </label>
                      <input
                        type="url"
                        value={courseData.video}
                        onChange={(e) => {
                          // If user starts typing a URL, clear any selected file preview
                          if (e.target.value && videoFile) {
                            setVideoFile(null);
                            setVideoPreview(null);
                          }
                          handleBasicInfoChange('video', e.target.value);
                        }}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        If you upload a file and also enter a URL, the uploaded file will be used.
                      </p>
                    </div>
                  </div>

                  {/* Subtitle Language Selection - Subtitles are automatically generated */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subtitle Language {videoFile && <span className="text-xs text-green-600">(will be generated automatically)</span>}
                    </label>
                    <select
                      value={courseData.subtitleLanguage || 'fr'}
                      onChange={(e) => handleBasicInfoChange('subtitleLanguage', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="fr">Français</option>
                      <option value="en">English</option>
                      <option value="ar">العربية</option>
                      <option value="es">Español</option>
                    </select>
                    {videoFile && (
                      <p className="text-xs text-gray-500 mt-2">
                        Subtitles will be automatically generated from the video in the selected language.
                      </p>
                    )}
                  </div>

                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={courseData.isFree}
                        onChange={(e) => {
                          handleBasicInfoChange('isFree', e.target.checked);
                          if (e.target.checked) {
                            handleBasicInfoChange('price', 0);
                          }
                        }}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Free Course</span>
                    </label>
                    {!courseData.isFree && (
                      <input
                        type="number"
                        value={courseData.price}
                        onChange={(e) => handleBasicInfoChange('price', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Price in $"
                      />
                    )}
                  </div>

                  <div>
                    <label className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={courseData.certificateAvailable}
                        onChange={(e) => handleBasicInfoChange('certificateAvailable', e.target.checked)}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Certificate Available</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={courseData.isPublished}
                        onChange={(e) => handleBasicInfoChange('isPublished', e.target.checked)}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Publish Course</span>
                    </label>
                    <p className="text-xs text-gray-500 ml-6">
                      Published courses will be visible in the calendar and course listings
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Add a tag"
                    />
                    <button
                      type="button"
                      onClick={addTag}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {courseData.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="hover:text-green-900"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Downloadable PDFs */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Downloadable PDFs
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setCourseData(prev => ({
                          ...prev,
                          downloadablePDFs: [...prev.downloadablePDFs, { title: '', url: '', description: '' }]
                        }));
                      }}
                      className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700"
                    >
                      <Plus className="h-4 w-4" />
                      Add PDF
                    </button>
                  </div>
                  {courseData.downloadablePDFs.map((pdf, index) => (
                    <div key={index} className="flex gap-2 mb-2 p-3 bg-gray-50 rounded-lg">
                      <input
                        type="text"
                        value={pdf.title}
                        onChange={(e) => {
                          const newPDFs = [...courseData.downloadablePDFs];
                          newPDFs[index] = { ...newPDFs[index], title: e.target.value };
                          setCourseData(prev => ({ ...prev, downloadablePDFs: newPDFs }));
                        }}
                        placeholder="PDF Title"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                      />
                      <input
                        type="url"
                        value={pdf.url}
                        onChange={(e) => {
                          const newPDFs = [...courseData.downloadablePDFs];
                          newPDFs[index] = { ...newPDFs[index], url: e.target.value };
                          setCourseData(prev => ({ ...prev, downloadablePDFs: newPDFs }));
                        }}
                        placeholder="PDF URL"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                      />
                      <input
                        type="text"
                        value={pdf.description}
                        onChange={(e) => {
                          const newPDFs = [...courseData.downloadablePDFs];
                          newPDFs[index] = { ...newPDFs[index], description: e.target.value };
                          setCourseData(prev => ({ ...prev, downloadablePDFs: newPDFs }));
                        }}
                        placeholder="Description"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setCourseData(prev => ({
                            ...prev,
                            downloadablePDFs: prev.downloadablePDFs.filter((_, i) => i !== index)
                          }));
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Slides */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Slides
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setCourseData(prev => ({
                          ...prev,
                          slides: [...prev.slides, { title: '', url: '', description: '', slideNumber: prev.slides.length + 1 }]
                        }));
                      }}
                      className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700"
                    >
                      <Plus className="h-4 w-4" />
                      Add Slide
                    </button>
                  </div>
                  {courseData.slides.map((slide, index) => (
                    <div key={index} className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Slide Number
                          </label>
                          <input
                            type="number"
                            value={slide.slideNumber || index + 1}
                            onChange={(e) => {
                              const newSlides = [...courseData.slides];
                              newSlides[index] = { ...newSlides[index], slideNumber: parseInt(e.target.value) || index + 1 };
                              setCourseData(prev => ({ ...prev, slides: newSlides }));
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Slide Title
                          </label>
                          <input
                            type="text"
                            value={slide.title}
                            onChange={(e) => {
                              const newSlides = [...courseData.slides];
                              newSlides[index] = { ...newSlides[index], title: e.target.value };
                              setCourseData(prev => ({ ...prev, slides: newSlides }));
                            }}
                            placeholder="Slide Title"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Slide Image
                        </label>
                        {slideImagePreviews[index] ? (
                          <div className="relative">
                            <img
                              src={slideImagePreviews[index]!}
                              alt={`Slide ${index + 1} preview`}
                              className="w-full h-48 object-contain border border-gray-300 rounded-lg mb-2"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setSlideImageFiles(prev => {
                                  const newFiles = { ...prev };
                                  delete newFiles[index];
                                  return newFiles;
                                });
                                setSlideImagePreviews(prev => {
                                  const newPreviews = { ...prev };
                                  delete newPreviews[index];
                                  return newPreviews;
                                });
                                const newSlides = [...courseData.slides];
                                newSlides[index] = { ...newSlides[index], url: '' };
                                setCourseData(prev => ({ ...prev, slides: newSlides }));
                              }}
                              className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : slide.url ? (
                          <div className="relative">
                            <img
                              src={slide.url}
                              alt={`Slide ${index + 1}`}
                              className="w-full h-48 object-contain border border-gray-300 rounded-lg mb-2"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newSlides = [...courseData.slides];
                                newSlides[index] = { ...newSlides[index], url: '' };
                                setCourseData(prev => ({ ...prev, slides: newSlides }));
                              }}
                              className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <Upload className="h-8 w-8 text-gray-400 mb-2" />
                              <p className="text-sm text-gray-500">Click to upload slide image</p>
                              <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 5MB</p>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  setSlideImageFiles(prev => ({ ...prev, [index]: file }));
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    setSlideImagePreviews(prev => ({ ...prev, [index]: reader.result as string }));
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                              className="hidden"
                            />
                          </label>
                        )}
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          value={slide.description}
                          onChange={(e) => {
                            const newSlides = [...courseData.slides];
                            newSlides[index] = { ...newSlides[index], description: e.target.value };
                            setCourseData(prev => ({ ...prev, slides: newSlides }));
                          }}
                          placeholder="Slide description"
                          rows={2}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setCourseData(prev => ({
                            ...prev,
                            slides: prev.slides.filter((_, i) => i !== index)
                          }));
                          setSlideImageFiles(prev => {
                            const newFiles = { ...prev };
                            delete newFiles[index];
                            return newFiles;
                          });
                          setSlideImagePreviews(prev => {
                            const newPreviews = { ...prev };
                            delete newPreviews[index];
                            return newPreviews;
                          });
                        }}
                        className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove Slide
                      </button>
                    </div>
                  ))}
                </div>

                {/* External Links & Resources */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      External Links & Resources
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setCourseData(prev => ({
                          ...prev,
                          externalLinks: [...prev.externalLinks, { title: '', url: '', description: '', type: 'website' }]
                        }));
                      }}
                      className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700"
                    >
                      <Plus className="h-4 w-4" />
                      Add Link
                    </button>
                  </div>
                  {courseData.externalLinks.map((link, index) => (
                    <div key={index} className="flex gap-2 mb-2 p-3 bg-gray-50 rounded-lg">
                      <input
                        type="text"
                        value={link.title}
                        onChange={(e) => {
                          const newLinks = [...courseData.externalLinks];
                          newLinks[index] = { ...newLinks[index], title: e.target.value };
                          setCourseData(prev => ({ ...prev, externalLinks: newLinks }));
                        }}
                        placeholder="Link Title"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                      />
                      <input
                        type="url"
                        value={link.url}
                        onChange={(e) => {
                          const newLinks = [...courseData.externalLinks];
                          newLinks[index] = { ...newLinks[index], url: e.target.value };
                          setCourseData(prev => ({ ...prev, externalLinks: newLinks }));
                        }}
                        placeholder="URL"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                      />
                      <select
                        value={link.type}
                        onChange={(e) => {
                          const newLinks = [...courseData.externalLinks];
                          newLinks[index] = { ...newLinks[index], type: e.target.value };
                          setCourseData(prev => ({ ...prev, externalLinks: newLinks }));
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="article">Article</option>
                        <option value="video">Video</option>
                        <option value="document">Document</option>
                        <option value="website">Website</option>
                        <option value="other">Other</option>
                      </select>
                      <input
                        type="text"
                        value={link.description}
                        onChange={(e) => {
                          const newLinks = [...courseData.externalLinks];
                          newLinks[index] = { ...newLinks[index], description: e.target.value };
                          setCourseData(prev => ({ ...prev, externalLinks: newLinks }));
                        }}
                        placeholder="Description"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setCourseData(prev => ({
                            ...prev,
                            externalLinks: prev.externalLinks.filter((_, i) => i !== index)
                          }));
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Lessons Tab */}
            {activeTab === 'lessons' && (
              <div className="space-y-6">
                {/* Existing Lessons List */}
                {courseData.lessons.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Added Lessons</h3>
                    {courseData.lessons.map((lesson, index) => (
                      <div
                        key={index}
                        className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h4 className="font-semibold">Lesson {index + 1}: {lesson.title}</h4>
                            <p className="text-sm text-gray-600">{lesson.duration} min</p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => editLesson(index)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteLesson(index)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add/Edit Lesson Form */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">
                    {editingLessonIndex !== null ? 'Edit Lesson' : 'Add a New Lesson'}
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lesson Title *
                      </label>
                      <input
                        type="text"
                        value={currentLesson.title}
                        onChange={(e) => setCurrentLesson(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Ex: Introduction to Basic Concepts"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={currentLesson.description}
                        onChange={(e) => setCurrentLesson(prev => ({ ...prev, description: e.target.value }))}
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Lesson description"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Content (HTML) *
                      </label>
                      <textarea
                        value={currentLesson.content}
                        onChange={(e) => setCurrentLesson(prev => ({ ...prev, content: e.target.value }))}
                        required
                        rows={8}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-sm"
                        placeholder="HTML content of the lesson..."
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Lesson Video
                        </label>
                        {lessonVideoFile || currentLesson.videoUrl ? (
                          <div className="relative p-4 border-2 border-green-300 rounded-lg bg-green-50">
                            {lessonVideoPreview ? (
                              <video
                                src={lessonVideoPreview}
                                controls
                                className="w-full rounded-lg mb-2"
                                style={{ maxHeight: '200px' }}
                              />
                            ) : currentLesson.videoUrl ? (
                              <div className="flex items-center gap-2 mb-2">
                                <Video className="h-5 w-5 text-green-600" />
                                <p className="text-sm font-medium text-green-800 truncate">
                                  {currentLesson.videoUrl}
                                </p>
                              </div>
                            ) : null}
                            <button
                              type="button"
                              onClick={removeLessonVideo}
                              className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                            {lessonVideoFile && (
                              <p className="text-xs text-gray-500 mt-1">
                                {lessonVideoFile.name} ({(lessonVideoFile.size / (1024 * 1024)).toFixed(2)} MB)
                              </p>
                            )}
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <Video className="h-8 w-8 text-gray-400 mb-2" />
                              <p className="text-sm text-gray-500">Click to upload video</p>
                              <p className="text-xs text-gray-500 mt-1">MP4, WebM, OGG up to 500MB</p>
                            </div>
                            <input
                              type="file"
                              accept="video/*"
                              onChange={handleLessonVideoChange}
                              className="hidden"
                            />
                          </label>
                        )}
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Or provide a video URL for this lesson
                          </label>
                          <input
                            type="url"
                            value={currentLesson.videoUrl}
                            onChange={(e) => {
                              const value = e.target.value;
                              // If user starts typing a URL, clear any selected file preview
                              if (value && lessonVideoFile) {
                                setLessonVideoFile(null);
                                setLessonVideoPreview(null);
                              }
                              setCurrentLesson(prev => ({ ...prev, videoUrl: value }));
                            }}
                            placeholder="https://www.youtube.com/watch?v=..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Duration (minutes) *
                        </label>
                        <input
                          type="number"
                          value={currentLesson.duration}
                          onChange={(e) => setCurrentLesson(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                          min="0"
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Lesson Subtitle Language - Subtitles are automatically generated */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subtitle Language {lessonVideoFile && <span className="text-xs text-green-600">(will be generated automatically)</span>}
                      </label>
                      <select
                        value={currentLesson.subtitleLanguage || 'fr'}
                        onChange={(e) => setCurrentLesson(prev => ({ ...prev, subtitleLanguage: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="fr">Français</option>
                        <option value="en">English</option>
                        <option value="ar">العربية</option>
                        <option value="es">Español</option>
                      </select>
                      {lessonVideoFile && (
                        <p className="text-xs text-gray-500 mt-2">
                          Subtitles will be automatically generated from the lesson video in the selected language.
                        </p>
                      )}
                    </div>

                    {/* Resources */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Resources
                        </label>
                        <button
                          type="button"
                          onClick={addResource}
                          className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700"
                        >
                          <Plus className="h-4 w-4" />
                          Add Resource
                        </button>
                      </div>
                      {currentLesson.resources.map((resource, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={resource.title}
                            onChange={(e) => updateResource(index, 'title', e.target.value)}
                            placeholder="Title"
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                          />
                          <input
                            type="url"
                            value={resource.url}
                            onChange={(e) => updateResource(index, 'url', e.target.value)}
                            placeholder="URL"
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                          />
                          <select
                            value={resource.type}
                            onChange={(e) => updateResource(index, 'type', e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg"
                          >
                            <option value="pdf">PDF</option>
                            <option value="video">Video</option>
                            <option value="link">Link</option>
                            <option value="document">Document</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => deleteResource(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Quiz */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <label className="block text-sm font-medium text-gray-700">
                          Lesson Quiz
                        </label>
                      </div>

                      {/* Questions List */}
                      {currentLesson.quiz.questions.length > 0 && (
                        <div className="space-y-3 mb-4">
                          {currentLesson.quiz.questions.map((q, index) => (
                            <div key={index} className="p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium">Question {index + 1}</span>
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => editQuestion(index)}
                                    className="text-blue-600 hover:text-blue-700 text-sm"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteQuestion(index)}
                                    className="text-red-600 hover:text-red-700 text-sm"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                              <p className="text-sm">{q.question}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Question Form */}
                      <div className="border-t pt-4">
                        <h4 className="font-medium mb-3">
                          {editingQuizIndex !== null ? 'Edit Question' : 'Add Question'}
                        </h4>
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={currentQuestion.question}
                            onChange={(e) => setCurrentQuestion(prev => ({ ...prev, question: e.target.value }))}
                            placeholder="Question"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          />
                          {currentQuestion.options.map((option, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="correctAnswer"
                                checked={currentQuestion.correctAnswer === index}
                                onChange={() => setCurrentQuestion(prev => ({ ...prev, correctAnswer: index }))}
                                className="text-green-600"
                              />
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => {
                                  const newOptions = [...currentQuestion.options];
                                  newOptions[index] = e.target.value;
                                  setCurrentQuestion(prev => ({ ...prev, options: newOptions }));
                                }}
                                placeholder={`Option ${index + 1}`}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                              />
                            </div>
                          ))}
                          <input
                            type="text"
                            value={currentQuestion.explanation}
                            onChange={(e) => setCurrentQuestion(prev => ({ ...prev, explanation: e.target.value }))}
                            placeholder="Explanation (optional)"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          />
                          <div className="flex gap-2">
                            {editingQuizIndex !== null ? (
                              <button
                                type="button"
                                onClick={updateQuestion}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                              >
                                Update
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={addQuestion}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                              >
                                Add Question
                              </button>
                            )}
                            {editingQuizIndex !== null && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingQuizIndex(null);
                                  setCurrentQuestion({
                                    question: '',
                                    options: ['', '', '', ''],
                                    correctAnswer: 0,
                                    explanation: ''
                                  });
                                }}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-4 border-t">
                      {editingLessonIndex !== null ? (
                        <>
                          <button
                            type="button"
                            onClick={updateLesson}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            Update Lesson
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingLessonIndex(null);
                              setCurrentLesson({
                                title: '',
                                description: '',
                                content: '',
                                videoUrl: '',
                                duration: 0,
                                order: 0,
                                resources: [],
                                quiz: { questions: [] }
                              });
                            }}
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={addLesson}
                          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                        >
                          <Plus className="h-5 w-5" />
                          Add Lesson
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/courses')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                {isEditMode ? 'Update Course' : 'Create Course'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateCourse;

